import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { Plus, BookOpen, ChevronRight } from 'lucide-react';
import { cn, Button } from '@jeraisonne/ui';
import { SCHOOL_LEVELS, SUBJECTS } from '@jeraisonne/db';
import type { ContentStatus } from '@jeraisonne/db';
import { useAdminChapters } from '@/lib/admin/queries';
import type { AdminChapterSummary } from '@/lib/admin/queries';
import { useUpdateChapter } from '@/lib/admin/mutations';

const LEVEL_LABEL = Object.fromEntries(SCHOOL_LEVELS.map((l) => [l.value, l.label]));
const SUBJECT_LABEL = Object.fromEntries(SUBJECTS.map((s) => [s.value, s.label]));

const STATUS_STYLES: Record<ContentStatus, string> = {
  published: 'bg-xp-100 text-xp-700',
  draft: 'bg-ink-100 text-ink-600',
  archived: 'bg-accent-50 text-accent-600',
  awaiting_review: 'bg-amber-50 text-amber-700',
};

const STATUS_LABEL: Record<ContentStatus, string> = {
  published: 'Publié',
  draft: 'Brouillon',
  archived: 'Archivé',
  awaiting_review: 'En relecture',
};

type StatusFilter = 'all' | ContentStatus;

function ChapterRow({ chapter }: { chapter: AdminChapterSummary }) {
  const update = useUpdateChapter();

  function toggleStatus() {
    const next: ContentStatus = chapter.status === 'published' ? 'draft' : 'published';
    update.mutate({ id: chapter.id, status: next });
  }

  return (
    <div className="hover:bg-ink-50 group flex items-center gap-3 rounded-xl p-3 transition-colors">
      <Link
        to="/tutor/admin/chapters/$chapterId"
        params={{ chapterId: chapter.id }}
        className="flex min-w-0 flex-1 items-center gap-3"
      >
        <div className="bg-primary-50 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg">
          <BookOpen className="text-primary-500 h-4 w-4" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-display text-ink-900 truncate text-[14px] font-semibold">
            {chapter.title}
          </p>
          <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
            <span className="text-ink-500 font-sans text-[11px]">
              {SUBJECT_LABEL[chapter.subject] ?? chapter.subject}
            </span>
            <span className="text-ink-300">·</span>
            <span className="text-ink-500 font-sans text-[11px]">
              {LEVEL_LABEL[chapter.level] ?? chapter.level}
            </span>
            <span className="text-ink-300">·</span>
            <span className="text-ink-500 font-sans text-[11px]">
              {chapter.lesson_count} leçon{chapter.lesson_count !== 1 ? 's' : ''}
            </span>
            <span className="text-ink-300">·</span>
            <span className="text-ink-500 font-sans text-[11px]">
              {chapter.competence_count} compétence{chapter.competence_count !== 1 ? 's' : ''}
            </span>
          </div>
        </div>
        <span
          className={cn(
            'font-display shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold',
            STATUS_STYLES[chapter.status],
          )}
        >
          {STATUS_LABEL[chapter.status]}
        </span>
        <ChevronRight className="text-ink-300 group-hover:text-ink-500 h-4 w-4 shrink-0 transition-colors" />
      </Link>
      <button
        onClick={toggleStatus}
        disabled={update.isPending}
        className="text-primary-600 hover:text-primary-800 shrink-0 font-sans text-[11px] underline transition-opacity disabled:opacity-50"
      >
        {chapter.status === 'published' ? 'Dépublier' : 'Publier'}
      </button>
    </div>
  );
}

export function TutorAdminChaptersPage() {
  const { data: chapters = [], isLoading } = useAdminChapters();
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [collapsed, setCollapsed] = useState<Set<string>>(
    new Set(SCHOOL_LEVELS.map((l) => l.value)),
  );

  const filtered =
    statusFilter === 'all' ? chapters : chapters.filter((c) => c.status === statusFilter);

  const byLevel = SCHOOL_LEVELS.reduce<
    {
      level: string;
      label: string;
      bySubject: { subject: string; label: string; chs: AdminChapterSummary[] }[];
    }[]
  >((acc, l) => {
    const levelChs = filtered.filter((ch) => ch.level === l.value);
    if (levelChs.length === 0) return acc;
    const bySubject = SUBJECTS.reduce<
      { subject: string; label: string; chs: AdminChapterSummary[] }[]
    >((sacc, s) => {
      const chs = levelChs.filter((ch) => ch.subject === s.value);
      if (chs.length > 0) sacc.push({ subject: s.value, label: s.label, chs });
      return sacc;
    }, []);
    acc.push({ level: l.value, label: l.label, bySubject });
    return acc;
  }, []);

  function toggleLevel(level: string) {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(level)) next.delete(level);
      else next.add(level);
      return next;
    });
  }

  return (
    <div className="mx-auto max-w-3xl p-4 sm:p-6">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-ink-900 text-[22px] font-extrabold">
            Chapitres & cours
          </h1>
          <p className="text-ink-500 mt-0.5 font-sans text-[13px]">
            {chapters.length} chapitre{chapters.length !== 1 ? 's' : ''} au total
          </p>
        </div>
        <Link to="/tutor/admin/chapters/new">
          <Button size="sm" className="flex items-center gap-1.5">
            <Plus className="h-4 w-4" />
            <span className="hidden sm:inline">Nouveau chapitre</span>
            <span className="sm:hidden">Nouveau</span>
          </Button>
        </Link>
      </div>

      {/* Filters */}
      <div className="mb-5 flex flex-wrap gap-2">
        {(['all', 'published', 'draft', 'archived'] as StatusFilter[]).map((f) => (
          <button
            key={f}
            onClick={() => setStatusFilter(f)}
            className={cn(
              'font-display rounded-full px-3 py-1.5 text-[12px] font-semibold transition-colors',
              statusFilter === f
                ? 'bg-primary-600 text-white'
                : 'bg-ink-100 text-ink-600 hover:bg-ink-200',
            )}
          >
            {f === 'all' ? 'Tous' : STATUS_LABEL[f]}
          </button>
        ))}
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <div className="border-primary-500 h-8 w-8 animate-spin rounded-full border-2 border-t-transparent" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="py-16 text-center">
          <BookOpen className="text-ink-300 mx-auto mb-3 h-10 w-10" />
          <p className="font-display text-ink-600 font-semibold">Aucun chapitre</p>
          <p className="text-ink-400 mt-1 font-sans text-[13px]">
            Créez votre premier chapitre pour commencer.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {byLevel.map(({ level, label, bySubject }) => {
            const isCollapsed = collapsed.has(level);
            const total = bySubject.reduce((n, s) => n + s.chs.length, 0);
            return (
              <div key={level}>
                <button
                  onClick={() => toggleLevel(level)}
                  className="group flex w-full items-center gap-2 px-1 py-1"
                >
                  <ChevronRight
                    className={cn(
                      'text-ink-400 h-3.5 w-3.5 shrink-0 transition-transform duration-200',
                      !isCollapsed && 'rotate-90',
                    )}
                  />
                  <span className="font-display text-ink-400 group-hover:text-ink-600 text-[11px] font-bold uppercase tracking-widest transition-colors">
                    {label}
                  </span>
                  <span className="text-ink-300 ml-1 font-sans text-[11px]">
                    {total} chapitre{total !== 1 ? 's' : ''}
                  </span>
                </button>
                {!isCollapsed && (
                  <div className="mt-1 space-y-3">
                    {bySubject.map(({ subject, label: subjectLabel, chs }) => (
                      <div key={subject}>
                        <p className="font-display text-ink-400 mb-2 px-1 pl-6 text-[11px] font-bold uppercase tracking-widest">
                          {subjectLabel}
                        </p>
                        <div className="border-ink-200 divide-ink-100 divide-y overflow-hidden rounded-2xl border bg-white">
                          {chs.map((ch) => (
                            <ChapterRow key={ch.id} chapter={ch} />
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
